import type { JSX } from 'react';
import type { SourceTypeDefinition } from '../../core/definitions';

/** Original line icons drawn on a 16 px grid; stroke follows the text colour. */
const PATHS = {
  plus: 'M8 3v10M3 8h10',
  minus: 'M3 8h10',
  close: 'M4 4l8 8M12 4l-8 8',
  check: 'M3 8.5l3.2 3L13 4.5',
  trash: 'M3 4.5h10M6.5 4.5V3h3v1.5M4.5 4.5l.6 8.5h5.8l.6-8.5M7 7v4M9 7v4',
  eye: 'M1.5 8S4 3.5 8 3.5 14.5 8 14.5 8 12 12.5 8 12.5 1.5 8 1.5 8zM8 10a2 2 0 100-4 2 2 0 000 4z',
  eyeOff: 'M2 2l12 12M6.2 4A6.6 6.6 0 018 3.5C12 3.5 14.5 8 14.5 8a11 11 0 01-2 2.5M9.9 11.9A6 6 0 018 12.5C4 12.5 1.5 8 1.5 8a11 11 0 012.6-3',
  lock: 'M4 7.5h8v6H4zM5.5 7.5V5a2.5 2.5 0 015 0v2.5',
  unlock: 'M4 7.5h8v6H4zM5.5 7.5V5a2.5 2.5 0 014.8-1',
  gear: 'M8 10.2a2.2 2.2 0 100-4.4 2.2 2.2 0 000 4.4zM8 1.5v2M8 12.5v2M1.5 8h2M12.5 8h2M3.4 3.4l1.4 1.4M11.2 11.2l1.4 1.4M3.4 12.6l1.4-1.4M11.2 4.8l1.4-1.4',
  sliders: 'M2 4.5h7M12 4.5h2M2 11.5h2M7 11.5h7M10.5 3v3M5.5 10v3',
  up: 'M8 13V3M4 7l4-4 4 4',
  down: 'M8 3v10M4 9l4 4 4-4',
  external: 'M9 2h5v5M14 2L7 9M6 3H2v11h11v-4',
  chevronRight: 'M6 3.5L10.5 8 6 12.5',
  chevronDown: 'M3.5 6L8 10.5 12.5 6',
  folder: 'M1.5 4h4.2l1.3 1.6h7.5v7.4h-13z',
  image: 'M2 3h12v10H2zM2 10.5l3.5-3.5 3 3 2-2 3.5 3.5M10.5 6.2a.9.9 0 100-.01',
  camera: 'M1.5 5h8v6h-8zM9.5 7l5-2.5v7L9.5 9',
  monitor: 'M1.5 2.5h13v8.5h-13zM5.5 13.5h5M8 11v2.5',
  window: 'M2 3h12v10H2zM2 6h12M4 4.5h.01M6 4.5h.01',
  text: 'M3 4V3h10v1M8 3v10M6 13h4',
  music: 'M6 12V3.5l7-1.5v8.5M6 12a2 2 0 11-4 0 2 2 0 014 0zM13 10.5a2 2 0 11-4 0 2 2 0 014 0z',
  mic: 'M8 10a2 2 0 002-2V4a2 2 0 10-4 0v4a2 2 0 002 2zM4 8a4 4 0 008 0M8 12v2.5M5.5 14.5h5',
  speaker: 'M2 6h2.5L8 3v10L4.5 10H2zM10.5 5.5a3.5 3.5 0 010 5M12.3 3.7a6 6 0 010 8.6',
  speakerOff: 'M2 6h2.5L8 3v10L4.5 10H2zM10.5 6l4 4M14.5 6l-4 4',
  headphones: 'M2.5 12V8a5.5 5.5 0 0111 0v4M2.5 9.5h2.5v4H2.5zM11 9.5h2.5v4H11z',
  globe: 'M8 14.5a6.5 6.5 0 100-13 6.5 6.5 0 000 13zM1.5 8h13M8 1.5c2 2 2 11 0 13M8 1.5c-2 2-2 11 0 13',
  film: 'M2 2.5h12v11H2zM5 2.5v11M11 2.5v11M2 6h3M2 10h3M11 6h3M11 10h3',
  droplet: 'M8 2s4.5 4.6 4.5 7.8a4.5 4.5 0 01-9 0C3.5 6.6 8 2 8 2z',
  users: 'M6 7.5a2.5 2.5 0 100-5 2.5 2.5 0 000 5zM1.5 13.5a4.5 4.5 0 019 0M11 7a2 2 0 100-4M12 10a3.5 3.5 0 012.5 3.5',
  puzzle: 'M3 6h2.2a1.6 1.6 0 113.2 0H11v3h-1a1.6 1.6 0 100 3.2h1V14H3z',
  layers: 'M8 2l6.5 3.5L8 9 1.5 5.5zM1.5 8.5L8 12l6.5-3.5M1.5 11L8 14.5 14.5 11',
  group: 'M2 2h4v4H2zM10 10h4v4h-4zM6 4h6v6M4 6v6h6',
  copy: 'M5.5 5.5h8v8h-8zM3 10.5h-.5v-8h8V3',
  record: 'M8 12.5a4.5 4.5 0 100-9 4.5 4.5 0 000 9z',
  broadcast: 'M8 9.5a1.5 1.5 0 100-3 1.5 1.5 0 000 3zM4.8 11.2a4.5 4.5 0 010-6.4M11.2 4.8a4.5 4.5 0 010 6.4M2.7 13.3a7.5 7.5 0 010-10.6M13.3 2.7a7.5 7.5 0 010 10.6',
  pause: 'M5 3.5v9M11 3.5v9',
  play: 'M4.5 3l8 5-8 5z',
  stop: 'M4 4h8v8H4z',
  split: 'M1.5 3h13v10h-13zM8 3v10',
  snapshot: 'M2 5h2.5L6 3h4l1.5 2H14v8H2zM8 11a2.2 2.2 0 100-4.4A2.2 2.2 0 008 11z',
  refresh: 'M13 8a5 5 0 11-1.5-3.6M13 2.5V5h-2.5',
  warning: 'M8 2l6.5 11.5h-13zM8 6.5v3.5M8 12h.01',
  dots: 'M3.5 8h.01M8 8h.01M12.5 8h.01',
  swap: 'M3 5.5h10M10 2.5l3 3-3 3M13 10.5H3M6 7.5l-3 3 3 3',
  link: 'M6.5 9.5l3-3M7 4.5l1.2-1.2a2.8 2.8 0 014 4L11 8.5M9 11.5l-1.2 1.2a2.8 2.8 0 01-4-4L5 7.5',
} as const;

export type IconName = keyof typeof PATHS;

export function Icon({ name, size = 16, title }: { name: IconName; size?: number; title?: string }): JSX.Element {
  return (
    <svg
      width={size}
      height={size}
      viewBox="0 0 16 16"
      fill="none"
      stroke="currentColor"
      strokeWidth={1.4}
      strokeLinecap="round"
      strokeLinejoin="round"
      role={title ? 'img' : undefined}
      aria-label={title}
      aria-hidden={title ? undefined : true}
      focusable="false"
    >
      <path d={PATHS[name]} />
    </svg>
  );
}

const KIND_HINTS: readonly [RegExp, IconName][] = [
  [/camera|webcam/, 'camera'],
  [/display|screen/, 'monitor'],
  [/window|browser|iframe/, 'window'],
  [/text|label|clock|timer/, 'text'],
  [/image|slide|photo/, 'image'],
  [/media|video|film/, 'film'],
  [/mic|input/, 'mic'],
  [/audio|tone|music/, 'music'],
  [/color|colour|gradient/, 'droplet'],
  [/guest|remote|peer/, 'users'],
];

const CATEGORY_ICONS: Record<NonNullable<SourceTypeDefinition['category']>, IconName> = {
  capture: 'camera',
  media: 'film',
  graphics: 'image',
  network: 'globe',
  utility: 'gear',
  plugin: 'puzzle',
};

export function iconForSourceKind(kind: string, category?: SourceTypeDefinition['category']): IconName {
  const lower = kind.toLowerCase();
  for (const [pattern, icon] of KIND_HINTS) if (pattern.test(lower)) return icon;
  return category ? CATEGORY_ICONS[category] : 'puzzle';
}
